/**
 * The site's sound (Oud campaign, snippets/dossier-oud-launch.liquid): one switch for everything
 * audible, chosen once per visit (the first-landing overlay, then the sticky toggle). A visit ends
 * after VISIT_IDLE_MS without activity; the next one starts muted and asks again. Same engine as the
 * US site (app/components/sound/*).
 *
 * Browsers only let a page make sound after the visitor has clicked, tapped or pressed a key on it
 * (scrolling doesn't count), so videos registered with registerSoundVideo play muted until sound is
 * on and the AudioContext runs, then their sound fades in (through a gain node: iOS ignores media
 * volume).
 *
 * window.DossierSound: getState, subscribe, setSoundOn, registerSoundVideo, fixedHeaderBottom; a
 * 'dossier-sound:ready' event on window once it exists.
 */
(function () {
  if (window.DossierSound) return;

  const STORAGE_KEY = 'dossier-sound';
  const VISIT_IDLE_MS = 30 * 60 * 1000; // a new visit after 30 minutes without activity
  const VISIT_TOUCH_MS = 60 * 1000; // how often activity refreshes the visit
  const VIDEO_FADE_IN_S = 3;
  const VIDEO_FADE_OUT_S = 0.3;
  // a video that pauses this soon after being unmuted was stopped by the browser for having sound
  const REFUSED_WITHIN_MS = 1000;
  // --- the switch

  let state = { on: false };
  let lastTouch = 0;
  const listeners = new Set();

  function subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  function setState(next) {
    if (next.on === state.on) return;
    state = next;
    save();
    listeners.forEach((listener) => listener(state));
  }

  function load() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
      if (saved && Date.now() - Number(saved.seen) < VISIT_IDLE_MS) state = { on: !!saved.on };
    } catch {
      // storage blocked: every page load is a new visit
    }
    save();
  }

  function save() {
    lastTouch = Date.now();
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, seen: lastTouch }));
    } catch {
      // storage blocked
    }
  }

  /** Activity: keeps the visit going, or starts a new one after a long idle. */
  function touchVisit() {
    const now = Date.now();
    if (now - lastTouch < VISIT_TOUCH_MS) return;
    if (now - lastTouch > VISIT_IDLE_MS) {
      for (const entry of videos) silence(entry, 0);
      setState({ on: false });
    }
    save();
  }

  /** Turns all the site's sound on or off (a click on the overlay or the toggle). */
  function setSoundOn(on) {
    setState({ on });
    if (on) unlock();
    else {
      for (const entry of videos) silence(entry, VIDEO_FADE_OUT_S);
    }
  }

  // --- audio

  let context = null;
  let output = null;

  function audioContext() {
    if (context) return context;
    const Context = window.AudioContext ?? window.webkitAudioContext;
    if (!Context) return null;
    context = new Context();
    output = context.createGain();
    output.connect(context.destination);
    return context;
  }

  /** In a gesture with sound on: start the audio and let the videos be heard. */
  function unlock() {
    const ctx = audioContext();
    if (!ctx) return;
    if (ctx.state !== 'running') ctx.resume().catch(() => undefined);
    for (const entry of videos) applyVideo(entry, true);
  }

  function onGesture() {
    touchVisit();
    if (state.on) unlock();
  }

  // --- videos

  const videos = new Set();
  // an element can only be routed into the audio graph once
  const graphs = new WeakMap();

  function gainFor(video) {
    const ctx = context;
    if (!ctx || !output) return null;
    let gain = graphs.get(video);
    if (!gain) {
      gain = ctx.createGain();
      gain.gain.value = 0;
      ctx.createMediaElementSource(video).connect(gain).connect(output);
      graphs.set(video, gain);
    }
    return gain;
  }

  /** Brings in the video's sound if it should be heard. */
  function applyVideo(entry, inGesture) {
    const { video } = entry;
    if (!state.on || video.paused) {
      silence(entry, 0);
      return;
    }
    if (entry.audible) return;
    // no gesture with sound on yet: stays muted until there is one
    const gain = gainFor(video);
    if (!gain || !context) return;
    const t = context.currentTime;
    gain.gain.cancelScheduledValues(t);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(1, t + VIDEO_FADE_IN_S);
    entry.audible = true;
    entry.unmutedAt = inGesture ? 0 : performance.now();
    video.muted = false;
  }

  /** Takes the video's sound away (fading it out first, or right away). */
  function silence(entry, fadeS) {
    const { video } = entry;
    entry.audible = false;
    const gain = graphs.get(video);
    if (!gain || !context || !fadeS || video.muted) {
      if (gain && context) gain.gain.setValueAtTime(0, context.currentTime);
      video.muted = true;
      return;
    }
    const t = context.currentTime;
    gain.gain.cancelScheduledValues(t);
    gain.gain.setValueAtTime(gain.gain.value, t);
    gain.gain.linearRampToValueAtTime(0, t + fadeS);
    window.setTimeout(() => {
      if (!entry.audible) video.muted = true;
    }, fadeS * 1000);
  }

  /**
   * Lets the sound switch control a video: it plays muted until sound is on (and the browser allows
   * it), then its sound fades in, again every time it resumes. The owner plays and pauses it.
   * `wanted()`: whether its owner wants it playing. Needs crossOrigin on the video (CORS).
   */
  function registerSoundVideo(video, wanted) {
    video.muted = true;
    const entry = {
      video,
      wanted,
      audible: false,
      unmutedAt: 0,
      onPlaying: () => applyVideo(entry, false),
      onPause: () => {
        const refused =
          entry.audible && entry.unmutedAt && performance.now() - entry.unmutedAt < REFUSED_WITHIN_MS && entry.wanted();
        silence(entry, 0);
        // the browser wants a gesture for sound (Safari, outside a tap): play on muted, the next tap
        // brings the sound in
        if (refused) video.play().catch(() => undefined);
      },
    };
    video.addEventListener('playing', entry.onPlaying);
    video.addEventListener('pause', entry.onPause);
    videos.add(entry);
    applyVideo(entry, false);
    return () => {
      video.removeEventListener('playing', entry.onPlaying);
      video.removeEventListener('pause', entry.onPause);
      videos.delete(entry);
      silence(entry, 0);
    };
  }

  // --- the header

  const STEP = 6;
  const MAX = 240;
  const MAX_HEADER_PART = 200; // an announcement bar or nav, not e.g. the PDP's sticky gallery

  const paints = (element) => {
    if (/^(img|svg|video|canvas)$/i.test(element.tagName)) return true;
    const style = getComputedStyle(element);
    const alpha = style.backgroundColor.match(/[\d.]+/g)?.[3];
    return (
      (style.backgroundColor !== 'transparent' && (alpha === undefined || Number(alpha) > 0.5)) ||
      style.backgroundImage !== 'none' ||
      style.backdropFilter !== 'none'
    );
  };

  /** Whether the point is covered by a painting, header-sized fixed/sticky element. */
  function coveredAt(x, y) {
    let node = document.elementFromPoint(x, y);
    let painted = false;
    while (node && node !== document.body) {
      painted ||= paints(node);
      const { position } = getComputedStyle(node);
      if (position === 'fixed' || position === 'sticky') {
        const rect = node.getBoundingClientRect();
        return painted && rect.top <= y && rect.height < MAX_HEADER_PART;
      }
      node = node.parentElement;
    }
    return false;
  }

  /**
   * Bottom edge (CSS px) of the site's fixed/sticky header (announcement bar + nav), found by
   * hit-testing down the middle of the viewport until the point is no longer covered by a fixed or
   * sticky, header-sized element that paints there. Lets pinned sections and the sound toggle sit
   * just below the header whether it's showing or not.
   */
  function fixedHeaderBottom() {
    const x = window.innerWidth / 2;
    // coarse steps down to the first uncovered point, then pixel by pixel back from there
    let y = 1;
    while (y < MAX && coveredAt(x, y)) y += STEP;
    if (y === 1) return 0;
    let bottom = y - STEP;
    while (bottom + 1 < y && coveredAt(x, bottom + 1)) bottom++;
    return bottom + 1;
  }

  window.DossierSound = {
    getState: () => state,
    subscribe,
    setSoundOn,
    registerSoundVideo,
    fixedHeaderBottom,
  };
  // scripts that ran before this one (it loads at the end of the page) wait for this to use it
  window.dispatchEvent(new Event('dossier-sound:ready'));

  // --- start: listens for gestures so videos can play with sound when enabled

  load();
  window.addEventListener('pointerdown', onGesture, { capture: true, passive: true });
  for (const type of ['touchend', 'keydown']) window.addEventListener(type, onGesture, { capture: true, passive: true });
  window.addEventListener('scroll', touchVisit, { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') save();
    else touchVisit();
  });

  // ===============================================================================================
  // Sticky sound switch on every page: a speaker while the sound is on, crossed out while muted.

  class SoundToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.label = this.querySelector('.sound-toggle__label');
      this.button.addEventListener('click', () => setSoundOn(!state.on));
      this.render(state);
      this.unsubscribe = subscribe((next) => this.render(next));
      this.stopPlacement = this.watchPlacement();
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      this.stopPlacement?.();
    }

    /**
     * Only shown while it sits over the Oud collection (<oud-collection>): hidden on every other
     * page and as soon as the page scrolls it outside that box. Also hidden while a section with
     * its own sound control is on screen ([data-sound-controls], the homepage hero). There it's
     * over the dark footage, so the label is white (.is-over-media).
     */
    watchPlacement() {
      const ownControls = new Set();
      let frame = 0;

      const update = () => {
        frame = 0;
        // no hit-test (it forces a layout) on pages without the collection
        const rect = document.querySelector('oud-collection') && this.getBoundingClientRect();
        const over =
          !!rect &&
          rect.width > 0 &&
          document
            .elementsFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2)
            .some((element) => !this.contains(element) && element.closest('oud-collection'));
        this.classList.toggle('is-hidden', !over || ownControls.size > 0);
        this.classList.toggle('is-over-media', over);
      };
      const schedule = () => {
        if (!frame) frame = requestAnimationFrame(update);
      };

      const observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) ownControls.add(entry.target);
          else ownControls.delete(entry.target);
        }
        schedule();
      });
      document.querySelectorAll('[data-sound-controls]').forEach((target) => observer.observe(target));

      // hidden until the first check says where it is
      this.classList.add('is-hidden');
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      // content that loads or expands under it without a scroll (lazy sections, accordions, tabs)
      const interval = window.setInterval(schedule, 1000);
      schedule();

      return () => {
        observer.disconnect();
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', schedule);
        window.clearInterval(interval);
        if (frame) cancelAnimationFrame(frame);
      };
    }

    render({ on }) {
      this.button.setAttribute('aria-pressed', String(on));
      this.button.setAttribute('aria-label', on ? this.dataset.turnOff : this.dataset.turnOn);
      this.classList.toggle('is-on', on);
      this.label.textContent = on ? this.dataset.unmuted : this.dataset.muted;
    }
  }

  customElements.define('sound-toggle', SoundToggle);
})();
